<template>
  <div class="service-provider-configuration">
    <service-provider-configuration-header
      :display-add-employee='serviceProviders.length > 0'
      @newEmployeeClicked="navigateToNewEmployee"
    />
    <add-new-service-provider-employee-layout
      @onCloseAddNewSPEmployeePopup='onCloseAddNewSPEmployeePopup'
      :visible-popup='toggleAddNewSPEmployeePopup'
    />
    <div class="service-provider-configuration__header" >
      <el-radio-group v-model="activeTab" size="small">
        <el-radio-button
          class="tab-button"
          v-for="tab in tabs"
          :label="tab.label"
          :key="tab.label"
        />
      </el-radio-group>
      <v-text-field
        v-if="serviceProviders.length"
        :append-icon="search?'close':'search'"
        @click:append="clearSearch"
        @keydown.native.escape="clearSearch"
        label="Search"
        solo
        v-model="search"
      ></v-text-field>
    </div>
    <div v-if="serviceProviders.length">
      <wombat-table
        class="service-providers-table"
        :items="serviceProviderDataList"
        :columns="tableColumns"
        :resize="false"
        :loadingItems="false"
        :columnsReorder="false"
        :itemHeight="58"
        :noResultsMessage="$t('noResultsFound')"
      >
        <component
          @deleteServiceProviderEmployee='deleteServiceProviderEmployee'
          @navigateToEditEmployee='navigateToEditEmployee'
          @editActivationOfEmployee='editActivationOfEmployee'
          slot="row-cell"
          slot-scope="rowCell"
          class="row-cell"
          :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
          :item="rowCell.item"
          :column="rowCell.column"
        />
      </wombat-table>
    </div>
    <div class='no-employees-section' v-else>
      <span class='no-employees'> {{ $t('service.provider.configuration.no.employees') }} </span>
      <v-btn class="new-employee-button" color="primary" @click="navigateToNewEmployee">
        {{ $t('service.provider.configuration.add.new.employee') }}
      </v-btn>
    </div>
    <edit-s-p-employee-profile-layout
      v-if='currentSPEmployee'
      @onCloseEditProfilePopup='onCloseEditProfilePopup'
      :visible-popup='toggleEditEmployeeProfilePopup'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import { getEmptyColumn, serviceProviderConfigurationColumns } from '@/services/tableColumns';
import {
  CHANGE_SERVICE_PROVIDER_EMPLOYEE_ACTIVE_STATUS,
  DELETE_SERVICE_PROVIDER_EMPLOYEE_USER,
  LOAD_CHOSEN_SERVICE_PROVIDER_EMPLOYEE_ID, LOAD_CURRENT_SERVICE_PROVIDER_EMPLOYEE, LOAD_SERVICE_PROVIDER_EMPLOYEE_BEFORE_EDIT,
  LOAD_SERVICE_PROVIDERS_OF_COMPANY,
} from '@/store/user/actionTypes';
import ServiceProviderConfigurationHeader
from '@/views/service-provider-configuration/ServiceProviderConfigurationHeader.vue';
import ActionsTableCell from '@/components/tableCells/ServiceProvidersConfigurationList/ActionsTableCell.vue';
import EmailTableCell from '@/components/tableCells/ServiceProvidersConfigurationList/EmailTableCell.vue';
import UserNameTableCell from '@/components/tableCells/ServiceProvidersConfigurationList/UserNameTableCell.vue';
import EditSPEmployeeProfileLayout from '@/views/layouts/EditSPEmployeeProfileLayout.vue';
import AddNewServiceProviderEmployeeLayout from '@/views/layouts/AddNewServiceProviderEmployeeLayout.vue';

export default {
  name: 'ServiceProviderConfiguration',
  components: {
    AddNewServiceProviderEmployeeLayout,
    EditSPEmployeeProfileLayout,
    WombatTable,
    WombatRowDefaultCell,
    ServiceProviderConfigurationHeader,
  },
  async mounted() {
    await this.$store.dispatch(LOAD_SERVICE_PROVIDERS_OF_COMPANY);
  },
  computed: {
    ...mapGetters(['serviceProvidersOfCompanyList', 'chosenSPEmployeeId', 'currentSPEmployee']),
    isActiveEmployeesTab() {
      return this.activeTab === this.activeEmployees.label;
    },
    activeEmployees() {
      return {
        label: this.$t('service.provider.configuration.active.employees.tab'),
      };
    },
    notActiveEmployees() {
      return {
        label: this.$t('service.provider.configuration.not.active.employees.tab'),
      };
    },
    tabs() {
      return [this.activeEmployees, this.notActiveEmployees];
    },
    serviceProvidersFiltered() {
      return this.serviceProviders
        .filter(
          serviceProvider => (serviceProvider.userName.toLowerCase().includes(this.search.toLowerCase()) ||
            serviceProvider.email.toLowerCase().includes(this.search.toLowerCase())),
        );
    },
    serviceProviderDataList() {
      if (!this.search) {
        return this.serviceProviders;
      }
      return this.serviceProvidersFiltered;
    },
    serviceProviders() {
      const serviceProviders = [];
      this.serviceProvidersOfCompanyList.forEach(serviceProvider => {
        if (this.isActiveEmployeesTab) {
          if (serviceProvider.workerProfile?.isActive && serviceProvider.workerProfile?.isActive === true) {
            serviceProviders.push({
              uid: serviceProvider.id,
              userName: `${serviceProvider.lastName} ${serviceProvider.firstName}`,
              email: serviceProvider.email,
              id: serviceProvider.userNumber,
              phoneNumber: serviceProvider.phone,
              role: serviceProvider.workerProfile.jobTitle,
              logo: serviceProvider.avatarURL,
            });
          }
        } else if (!serviceProvider.workerProfile?.isActive || serviceProvider.workerProfile?.isActive === false) {
          serviceProviders.push({
            uid: serviceProvider.id,
            userName: `${serviceProvider.lastName} ${serviceProvider.firstName}`,
            email: serviceProvider.email,
            id: serviceProvider.userNumber,
            phoneNumber: serviceProvider.phone,
            role: serviceProvider.workerProfile.jobTitle,
            logo: serviceProvider.avatarURL,
          });
        }
      });
      return serviceProviders;
    },
    tableRowCellComponentsHash() {
      return {
        userName: UserNameTableCell,
        id: WombatRowDefaultCell,
        email: EmailTableCell,
        phoneNumber: WombatRowDefaultCell,
        role: WombatRowDefaultCell,
        actions: ActionsTableCell,
        default: WombatRowDefaultCell,
      };
    },
    activeTab: {
      set(tab) {
        this.tab = tab;
      },
      get() {
        return this.tab;
      },
    },
    tableColumns() {
      const columns = [
        serviceProviderConfigurationColumns.id,
        serviceProviderConfigurationColumns.userName,
        serviceProviderConfigurationColumns.role,
        serviceProviderConfigurationColumns.phoneNumber,
        serviceProviderConfigurationColumns.email,
        serviceProviderConfigurationColumns.actions,
      ]
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
        }));
      return [getEmptyColumn(32), ...columns, getEmptyColumn(85)];
    },
  },
  methods: {
    async editActivationOfEmployee(employeeId) {
      await this.$store.dispatch(CHANGE_SERVICE_PROVIDER_EMPLOYEE_ACTIVE_STATUS, employeeId);
    },
    async navigateToEditEmployee(employeeId) {
      await this.$store.dispatch(LOAD_CHOSEN_SERVICE_PROVIDER_EMPLOYEE_ID, employeeId);
      await this.$store.dispatch(LOAD_CURRENT_SERVICE_PROVIDER_EMPLOYEE, employeeId);
      await this.$store.dispatch(LOAD_SERVICE_PROVIDER_EMPLOYEE_BEFORE_EDIT, employeeId);
      this.toggleEditEmployeeProfilePopup = true;
    },
    onCloseEditProfilePopup() {
      this.toggleEditEmployeeProfilePopup = false;
    },
    onCloseAddNewSPEmployeePopup() {
      this.toggleAddNewSPEmployeePopup = false;
    },
    async deleteServiceProviderEmployee(employeeId) {
      await this.$store.dispatch(DELETE_SERVICE_PROVIDER_EMPLOYEE_USER, employeeId);
    },
    async navigateToNewEmployee() {
      this.toggleAddNewSPEmployeePopup = true;
    },
    clearSearch() {
      this.search = '';
    },
  },
  watch: {
    activeTab() {
      this.search = '';
    },
  },
  data() {
    return {
      tab: this.$t('service.provider.configuration.active.employees.tab'),
      search: '',
      toggleEditEmployeeProfilePopup: false,
      toggleAddNewSPEmployeePopup: false,
    };
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";

.service-provider-configuration {
  padding: $space-4 $space-8;

  &__header {
    width: 97%;
    padding: $space-4 $space-8;
    display: flex;
    margin-bottom: $space-8;
    justify-content: space-between;

    :deep(.v-input) {
      max-width: 20%;
    }

    :deep(.v-text-field) {
      margin-top: 0;
    }
  }
}

.no-employees-section {
  margin-top: 20%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.no-employees {
  height: rem(19);
  width: rem(425);
  color: var(--text-secondary);
  font-family: $default-application-font-regular;
  font-size: $font-size-md;
  letter-spacing: $letter-spacing-normal;
  line-height: $line-height-snug;
  text-align: center;
  padding-bottom: $space-9;
}

.new-employee-button {
  flex: 0 0 10;
}
</style>

<style lang="scss">
@import "~@/assets/styles/design-system";

.service-provider-configuration__header {
  .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
    box-shadow: none;
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-sm;
  }

  .v-input__icon.v-input__icon--append.v-input__icon--append {
    margin-top: 0;
  }
}
</style>
